/* global $, user */

export async function loadCustomSSOInfo() {
    return await $.getJSON("/query/customSSOInfo");
}

export async function loadSSOEmailDomain(email) {
    return await $.getJSON("/query/ssoDomain", { email });
}

export async function getSSODomainForCurrentUser() {
    return await $.getJSON("/query/ssoUserDomain");
}

export async function getApiKey(apiKey) {
    return $.get(`/query/apiKey/${apiKey}`, null, "JSON");
}

export async function createApiKey(workspaceId, groupId) {
    const payload = { workspaceId };
    if (groupId) {
        payload["projectGroupIds"] = [groupId];
    }
    return $.post("/query/createApiKey", payload, null, "JSON");
}

export async function revokeApiKey(apiKey) {
    return $.post("/query/revokeApiKey", { apiKey }, null, "JSON");
}

export async function rollApiKey(apiKey) {
    return $.post("/query/rollApiKey", { apiKey }, null, "JSON");
}

/** @param {string} apiKey @param {{ name?: string, scopes?: string[], default?: boolean, disabled?: boolean, revoked?: boolean, custom_metadata?: Record<string, string> }} options */
export async function updateApiKey(
    apiKey,
    { name, scopes, default: isDefault, disabled, revoked, custom_metadata }
) {
    const payload = { apiKey, name, scopes };
    if (isDefault !== undefined) {
        payload.default = isDefault;
    }
    if (disabled !== undefined) {
        payload.disabled = disabled;
    }
    if (revoked !== undefined) {
        payload.revoked = revoked;
    }
    if (custom_metadata !== undefined) {
        payload.custom_metadata = custom_metadata;
    }
    return $.ajax({
        url: "/query/updateApiKey",
        method: "POST",
        dataType: "json",
        contentType: "application/json",
        data: JSON.stringify(payload)
    });
}

/** @param {string} workspaceId @param {{ includeFolders?: boolean, includeDisabled?: boolean, folderIds?: string[] }} options */
export async function getAuthorizedApiKeysForWorkspace(
    workspaceId,
    { includeFolders = false, includeDisabled = false, folderIds = [] } = {}
) {
    return await $.post(`/query/getAuthorizedApiKeysForWorkspace`, {
        workspaceId,
        includeFolders,
        includeDisabled,
        folderIds
    });
}

/** @param {string} workflowId @param {{ includeFolders?: boolean, workflow?: { owner?: string, config?: string } }} options */
export async function getAuthorizedApiKeysForWorkflow(
    workflowId,
    { includeFolders = false, workflow } = {}
) {
    const payload = { workflowId, includeFolders };
    if (workflow) {
        payload.workflow = workflow;
    }
    return await $.ajax({
        url: "/query/getAuthorizedApiKeysForWorkflow",
        method: "POST",
        dataType: "json",
        contentType: "application/json",
        data: JSON.stringify(payload)
    });
}

export async function refreshUserSession() {
    const idTokenResult = await user.getIdTokenResult(true);
    await $.post("/session", { token: idTokenResult.token, uid: user.uid });
}

export async function getIdToken() {
    if (!user) return null;
    try {
        return await user.getIdToken();
    } catch {
        return null;
    }
}

export async function getAuthHeaders(additionalHeaders = {}) {
    const token = await getIdToken();
    return token ? { ...additionalHeaders, Authorization: `Bearer ${token}` } : additionalHeaders;
}

export async function setGuestRefreshToken(guestInfo) {
    return await $.post("/query/setGuestRefreshToken", guestInfo);
}

export async function setGuestAccessToken() {
    return await $.post("/query/setGuestAccessToken", {});
}

export async function getUserSSOGroups(userId, workspaceId) {
    return await $.getJSON("/query/getUserSSOGroups", { userId, workspaceId });
}

export async function whoami() {
    return await $.getJSON("/query/whoami");
}

export async function autoSetupCreatedWorkspace(workspaceId) {
    return await $.post("/query/autoSetupCreatedWorkspace", { workspaceId }, "JSON");
}

export default {
    loadCustomSSOInfo,
    loadSSOEmailDomain,
    createApiKey,
    getAuthorizedApiKeysForWorkspace,
    getAuthorizedApiKeysForWorkflow,
    revokeApiKey,
    rollApiKey,
    refreshUserSession,
    setGuestRefreshToken,
    setGuestAccessToken,
    whoami,
    autoSetupCreatedWorkspace,
    getIdToken,
    getAuthHeaders
};
